import React, {useState, useContext, useEffect} from 'react'
import {getDailyReport, syncPushAll, syncPullAll} from '../generic/crud'
import BedRockContext from '../../BedRockContext'
import {formatDateTime} from '../../utils/Utils'

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0.00'
    return '₹' + Number(value).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

// Quantities always render with 3 decimal places (Indian grouping), which also
// avoids floating-point artifacts like 7.3100000000000005 in summed totals.
const formatQuantity = (value) => {
    if (value === null || value === undefined) return '0.000'
    return Number(value).toLocaleString('en-IN', {minimumFractionDigits: 3, maximumFractionDigits: 3})
}

const formatDate = (dateStr) => {
    return formatDateTime(dateStr + 'T00:00:00')
}

// Split the formatted "DD Mon YYYY, hh:mm am/pm" into a date line and a time
// line for the transaction tables. Returns null when there is no usable date.
const formatEntryDateTime = (iso) => {
    if (!iso) return null
    const formatted = formatDateTime(iso)
    if (!formatted || formatted === '-') return null
    const [datePart, timePart] = formatted.split(', ')
    return {date: datePart, time: timePart || ''}
}

const getDateString = (daysAgo = 0) => {
    const d = new Date()
    d.setDate(d.getDate() - daysAgo)
    return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0')
}

// Print a single section by opening it in a popup that carries the app's
// stylesheets, so the printout contains only that block (no blank pages
// from the rest of the report).
const printSection = (sectionId) => {
    const el = document.getElementById(sectionId)
    if (!el) return
    const win = window.open('', 'PRINT', 'height=700,width=1000')
    if (!win) return
    const styles = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style')
    )
        .map((node) => node.outerHTML)
        .join('\n')
    win.document.write(
        '<html><head><title>Daily Report</title>' +
        styles +
        '<style>.no-print{display:none !important;}body{padding:16px;background:#fff;}</style>' +
        '</head><body>' +
        el.outerHTML +
        '</body></html>'
    )
    win.document.close()
    win.onload = () => {
        win.focus()
        win.print()
        win.close()
    }
}

const SectionPrintButton = ({sectionId}) => (
    <button
        type="button"
        className="btn btn-sm btn-icon btn-light-primary ms-auto no-print"
        title="Print this section"
        onClick={() => printSection(sectionId)}
    >
        <i className="ki-duotone ki-printer fs-4">
            <span className="path1"></span>
            <span className="path2"></span>
            <span className="path3"></span>
            <span className="path4"></span>
            <span className="path5"></span>
        </i>
    </button>
)

const SummaryCard = ({title, color, icon, count, amount, tax, total}) => (
    <div className="col-md-4 mb-5">
        <div className={`card border border-${color} border-dashed`}>
            <div className="card-body p-6">
                <div className="d-flex align-items-center mb-4">
                    <div className={`symbol symbol-40px me-3`}>
                        <div className={`symbol-label bg-light-${color}`}>
                            <i className={`ki-duotone ${icon} fs-2 text-${color}`}>
                                <span className="path1"></span>
                                <span className="path2"></span>
                                <span className="path3"></span>
                            </i>
                        </div>
                    </div>
                    <div className={`fs-4 fw-bold text-${color}`}>{title}</div>
                </div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-gray-600 fs-7">Transactions</span>
                    <span className="fw-bold fs-7">{count || 0}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-gray-600 fs-7">Amount</span>
                    <span className="fw-bold fs-7">{formatCurrency(amount)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-gray-600 fs-7">Tax</span>
                    <span className="fw-bold fs-7">{formatCurrency(tax)}</span>
                </div>
                <div className="separator my-3"></div>
                <div className="d-flex justify-content-between">
                    <span className={`fw-bold fs-6 text-${color}`}>Total</span>
                    <span className={`fw-bolder fs-5 text-${color}`}>{formatCurrency(total)}</span>
                </div>
            </div>
        </div>
    </div>
)

const LedgerChangesTable = ({ledgerChanges}) => {
    if (!ledgerChanges || ledgerChanges.length === 0) return null

    const totalSales = ledgerChanges.reduce((sum, l) => sum + (l.salesAmount || 0), 0)
    const totalPurchases = ledgerChanges.reduce((sum, l) => sum + (l.purchaseAmount || 0), 0)
    const totalExpenses = ledgerChanges.reduce((sum, l) => sum + (l.expenseAmount || 0), 0)
    const totalNet = ledgerChanges.reduce((sum, l) => sum + (l.netChange || 0), 0)

    return (
        <div className="card mb-5" id="print-ledger-changes">
            <div className="card-body p-6">
                <div className="d-flex align-items-center mb-4">
                    <div className="symbol symbol-35px me-3">
                        <div className="symbol-label bg-light-info">
                            <i className="ki-duotone ki-bank fs-3 text-info">
                                <span className="path1"></span>
                                <span className="path2"></span>
                            </i>
                        </div>
                    </div>
                    <h5 className="fw-bold mb-0 text-info">Ledger Amount Changes</h5>
                    <span className="badge badge-light-dark ms-2">{ledgerChanges.length}</span>
                    <SectionPrintButton sectionId="print-ledger-changes" />
                </div>
                <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3">
                        <thead>
                            <tr className="fw-bold text-muted bg-light">
                                <th className="ps-4 min-w-150px">Ledger</th>
                                <th className="text-end">Sales (In)</th>
                                <th className="text-end">Purchases (Out)</th>
                                <th className="text-end">Expenses (Out)</th>
                                <th className="text-end pe-4">Net Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ledgerChanges.map((ledger, index) => (
                                <tr key={index}>
                                    <td className="ps-4 fw-semibold text-gray-800">{ledger.ledgerName}</td>
                                    <td className="text-end text-success">
                                        {ledger.salesAmount ? formatCurrency(ledger.salesAmount) : '—'}
                                    </td>
                                    <td className="text-end text-primary">
                                        {ledger.purchaseAmount ? formatCurrency(ledger.purchaseAmount) : '—'}
                                    </td>
                                    <td className="text-end text-warning">
                                        {ledger.expenseAmount ? formatCurrency(ledger.expenseAmount) : '—'}
                                    </td>
                                    <td className={`text-end pe-4 fw-bold ${ledger.netChange >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {formatCurrency(ledger.netChange)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bolder bg-light">
                                <td className="ps-4">TOTAL</td>
                                <td className="text-end text-success">{formatCurrency(totalSales)}</td>
                                <td className="text-end text-primary">{formatCurrency(totalPurchases)}</td>
                                <td className="text-end text-warning">{formatCurrency(totalExpenses)}</td>
                                <td className={`text-end pe-4 ${totalNet >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {formatCurrency(totalNet)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    )
}

const OriginDot = ({createdOnClient}) => {
    const isClient = !!createdOnClient
    const color = isClient ? '#f6b100' : '#3e97ff'
    const label = isClient ? 'Created on Client' : 'Created on Server'
    return (
        <span
            title={label}
            aria-label={label}
            style={{
                display: 'inline-block',
                flexShrink: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                boxShadow: `0 0 4px ${color}`,
                marginRight: 6,
                verticalAlign: 'middle',
            }}
        />
    )
}

const DetailsTable = ({title, color, icon, items, showTax, showCollected, showLedger, showTransaction}) => {
    const sectionId = 'print-' + title.replace(/\s+/g, '-').toLowerCase()
    const partyLabel = title.toLowerCase().includes('purchase') ? 'Supplier' : 'Customer'
    return (
        <div className="card mb-5" id={sectionId}>
            <div className="card-body p-6">
                <div className="d-flex align-items-center mb-4">
                    <div className={`symbol symbol-35px me-3`}>
                        <div className={`symbol-label bg-light-${color}`}>
                            <i className={`ki-duotone ${icon} fs-3 text-${color}`}>
                                <span className="path1"></span>
                                <span className="path2"></span>
                                <span className="path3"></span>
                            </i>
                        </div>
                    </div>
                    <h5 className={`fw-bold mb-0 text-${color}`}>{title}</h5>
                    <span className="badge badge-light-dark ms-2">{items.length}</span>
                    <SectionPrintButton sectionId={sectionId} />
                </div>
                <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3 fs-7">
                        <thead>
                            <tr className="fw-bold text-muted bg-light">
                                {showTransaction ? (
                                    <>
                                        <th className="ps-4 min-w-90px">Date/Time</th>
                                        <th className="min-w-70px">Ticket</th>
                                        <th className="min-w-100px">{partyLabel}</th>
                                        <th className="min-w-80px">Vehicle</th>
                                        <th className="min-w-100px">Items</th>
                                        <th className="text-end">Qty</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="ps-4 min-w-90px">Date/Time</th>
                                        <th className="min-w-150px">Description</th>
                                    </>
                                )}
                                <th className="text-end">Amount</th>
                                {showTax && <th className="text-end">Tax</th>}
                                <th className="text-end">Total</th>
                                {showCollected && <th className="text-end">Collected</th>}
                                {showLedger && <th className="pe-4">Ledger</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const dt = formatEntryDateTime(item.entryDate)
                                return (
                                    <tr key={index}>
                                        {showTransaction ? (
                                            <>
                                                <td className="ps-4">
                                                    {dt ? (
                                                        <>
                                                            <div className="fw-semibold text-gray-800 fs-7">{dt.date}</div>
                                                            <div className="text-muted fs-8">{dt.time}</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500 fs-7">—</span>
                                                    )}
                                                </td>
                                                <td className="fs-7 text-gray-800">
                                                    <span style={{display: 'inline-flex', alignItems: 'baseline'}}>
                                                        <OriginDot createdOnClient={item.createdOnClient} />
                                                        <span>{item.referenceNumber ? '#' + item.referenceNumber : '—'}</span>
                                                    </span>
                                                </td>
                                                <td className="fs-7">
                                                    <div style={{maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                                        {item.partyName || '—'}
                                                    </div>
                                                </td>
                                                <td className="fs-7 text-gray-700">{item.vehicleNumber || '—'}</td>
                                                <td className="fs-7">
                                                    <div style={{maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                                        {item.lineItems && item.lineItems.length > 0
                                                            ? item.lineItems.map((li, i) => (
                                                                  <div key={i}>{li.itemName || '—'}</div>
                                                              ))
                                                            : item.itemsSummary || '—'}
                                                    </div>
                                                </td>
                                                <td className="text-end fs-7">
                                                    {item.lineItems && item.lineItems.length > 0 ? (
                                                        item.lineItems.map((li, i) => (
                                                            <div key={i} className="d-flex justify-content-end">
                                                                <span className="fw-semibold">{formatQuantity(li.quantity)}</span>
                                                                <span className="text-gray-500 fs-8 text-start" style={{width: 30, marginLeft: 4}}>{li.unitSymbol || ''}</span>
                                                            </div>
                                                        ))
                                                    ) : item.totalQuantity ? (
                                                        <>
                                                            <span className="fw-semibold">{formatQuantity(item.totalQuantity)}</span>
                                                            {item.unitSymbol && (
                                                                <span className="text-gray-500 fs-8 ms-1">{item.unitSymbol}</span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="ps-4">
                                                    {dt ? (
                                                        <>
                                                            <div className="fw-semibold text-gray-800 fs-7">{dt.date}</div>
                                                            <div className="text-muted fs-8">{dt.time}</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500 fs-7">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="fw-semibold text-gray-800" style={{display: 'flex', alignItems: 'baseline'}}>
                                                        <OriginDot createdOnClient={item.createdOnClient} />
                                                        <span>{item.narration || '—'}</span>
                                                    </div>
                                                    {item.voucherNumber && (
                                                        <span className="text-gray-500 fs-8">{item.voucherNumber}</span>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                        <td className="text-end">{formatCurrency(item.amount)}</td>
                                        {showTax && <td className="text-end">{formatCurrency(item.taxAmount)}</td>}
                                        <td className="text-end fw-bold">{formatCurrency(item.totalAmount)}</td>
                                        {showCollected && (
                                            <td className={`text-end fw-semibold ${(item.collectedAmount || 0) >= (item.invoiceTotal || item.totalAmount || 0) ? 'text-success' : 'text-danger'}`}>
                                                {formatCurrency(item.collectedAmount)}
                                            </td>
                                        )}
                                        {showLedger && <td className="pe-4 text-gray-700 fs-7">{item.ledgerName || '—'}</td>}
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bolder bg-light">
                                <td className="ps-4" colSpan={showTransaction ? 5 : 2}>TOTAL</td>
                                {showTransaction && <td className="text-end"></td>}
                                <td className="text-end">
                                    {formatCurrency(items.reduce((sum, i) => sum + (i.amount || 0), 0))}
                                </td>
                                {showTax && (
                                    <td className="text-end">
                                        {formatCurrency(items.reduce((sum, i) => sum + (i.taxAmount || 0), 0))}
                                    </td>
                                )}
                                <td className="text-end">
                                    {formatCurrency(items.reduce((sum, i) => sum + (i.totalAmount || 0), 0))}
                                </td>
                                {showCollected && (
                                    <td className="text-end text-success">
                                        {formatCurrency(items.reduce((sum, i) => sum + (i.collectedAmount || 0), 0))}
                                    </td>
                                )}
                                {showLedger && <td></td>}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    )
}

const ItemBreakdownTable = ({title, color, icon, items}) => {
    if (!items || items.length === 0) return null

    const totalAmount = items.reduce((sum, i) => sum + (i.amount || 0), 0)
    const totalTax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0)
    const grandTotal = items.reduce((sum, i) => sum + (i.totalAmount || 0), 0)
    const sectionId = 'print-' + title.replace(/\s+/g, '-').toLowerCase()

    return (
        <div className="card mb-5" id={sectionId}>
            <div className="card-body p-6">
                <div className="d-flex align-items-center mb-4">
                    <div className={`symbol symbol-35px me-3`}>
                        <div className={`symbol-label bg-light-${color}`}>
                            <i className={`ki-duotone ${icon} fs-3 text-${color}`}>
                                <span className="path1"></span>
                                <span className="path2"></span>
                                <span className="path3"></span>
                            </i>
                        </div>
                    </div>
                    <h5 className={`fw-bold mb-0 text-${color}`}>{title}</h5>
                    <span className="badge badge-light-dark ms-2">{items.length}</span>
                    <SectionPrintButton sectionId={sectionId} />
                </div>
                <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3">
                        <thead>
                            <tr className="fw-bold text-muted bg-light">
                                <th className="ps-4 min-w-50px">#</th>
                                <th className="min-w-150px">Item</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Rate</th>
                                <th className="text-end">Amount</th>
                                <th className="text-end">Tax</th>
                                <th className="text-end pe-4">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td className="ps-4 text-gray-600 fs-7">{index + 1}</td>
                                    <td className="fw-semibold text-gray-800">{item.itemName || '—'}</td>
                                    <td className="text-end">
                                        <span className="fw-semibold">{formatQuantity(item.quantity)}</span>
                                        {item.unitSymbol && (
                                            <span className="text-gray-500 fs-8 ms-1">{item.unitSymbol}</span>
                                        )}
                                    </td>
                                    <td className="text-end">{formatCurrency(item.rate)}</td>
                                    <td className="text-end">{formatCurrency(item.amount)}</td>
                                    <td className="text-end">{formatCurrency(item.taxAmount)}</td>
                                    <td className="text-end pe-4 fw-bold">{formatCurrency(item.totalAmount)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bolder bg-light">
                                <td className="ps-4" colSpan={2}>TOTAL</td>
                                <td className="text-end"></td>
                                <td className="text-end"></td>
                                <td className="text-end">{formatCurrency(totalAmount)}</td>
                                <td className="text-end">{formatCurrency(totalTax)}</td>
                                <td className="text-end pe-4">{formatCurrency(grandTotal)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    )
}

const DailyReportPage = () => {
    const myData = useContext(BedRockContext)
    const [date, setDate] = useState(getDateString(0))
    const [loading, setLoading] = useState(false)
    const [syncStatus, setSyncStatus] = useState('')
    const [reportData, setReportData] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        myData.setBaseData({ toolBarButtons: [] })
    }, [])

    const generateReport = async () => {
        setLoading(true)
        setError('')
        setReportData(null)

        try {
            // Step 1: Push all local data to server
            setSyncStatus('Pushing local data to server...')
            await syncPushAll()

            // Step 2: Pull latest data from server
            setSyncStatus('Pulling latest data from server...')
            await syncPullAll()

            // Step 3: Fetch the daily report from server
            setSyncStatus('Generating report...')
            const {data} = await getDailyReport(date)
            setReportData(data)
            setSyncStatus('')
        } catch (err) {
            console.error('Error generating daily report:', err)
            setError(err?.response?.data?.error || err.message || 'Failed to generate report')
            setSyncStatus('')
        } finally {
            setLoading(false)
        }
    }

    const handlePrint = () => {
        window.print()
    }

    const summary = reportData?.summary || {}

    return (
        <>
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    .card { border: 1px solid #ddd !important; box-shadow: none !important; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                }
                .print-only { display: none; }
            `}</style>

            {/* Controls - hidden when printing */}
            <div className="card mb-5 no-print">
                <div className="card-body p-5">
                    <div className="d-flex align-items-end gap-4 flex-wrap">
                        <div>
                            <label className="form-label fw-bold fs-7 mb-1">Report Date</label>
                            <select
                                className="form-select form-select-sm"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                disabled={loading}
                                style={{width: '180px'}}
                            >
                                <option value={getDateString(0)}>Today</option>
                                <option value={getDateString(1)}>Yesterday</option>
                            </select>
                        </div>
                        <div className="d-flex align-items-end gap-2">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={generateReport}
                                disabled={loading || !date}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {syncStatus || 'Loading...'}
                                    </>
                                ) : (
                                    <>
                                        <i className="ki-duotone ki-cloud-download fs-4 me-1">
                                            <span className="path1"></span>
                                            <span className="path2"></span>
                                        </i>
                                        Sync & Generate Report
                                    </>
                                )}
                            </button>
                            {reportData && (
                                <button className="btn btn-light-success btn-sm" onClick={handlePrint}>
                                    <i className="ki-duotone ki-printer fs-4 me-1">
                                        <span className="path1"></span>
                                        <span className="path2"></span>
                                        <span className="path3"></span>
                                        <span className="path4"></span>
                                        <span className="path5"></span>
                                    </i>
                                    Print
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="alert alert-danger no-print">
                    <i className="ki-duotone ki-information-5 fs-3 me-2">
                        <span className="path1"></span>
                        <span className="path2"></span>
                        <span className="path3"></span>
                    </i>
                    {error}
                </div>
            )}

            {/* Report Content */}
            {reportData && (
                <>
                    {/* Print Header - only visible when printing */}
                    <div className="print-only text-center mb-6">
                        <h2 className="fw-bolder">Daily Summary Report</h2>
                        <div className="text-gray-600 fs-5">{formatDate(reportData.date)}</div>
                        <div className="separator my-4"></div>
                    </div>

                    {/* Report Header */}
                    <div className="card mb-5">
                        <div className="card-body p-5">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h3 className="fw-bolder mb-1">Daily Summary Report</h3>
                                    <span className="text-gray-500 fs-6">{formatDate(reportData.date)}</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className={`badge badge-light-${summary.netAmount >= 0 ? 'success' : 'danger'} fs-5 p-3`}>
                                        Net: {formatCurrency(summary.netAmount)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="row">
                        <SummaryCard
                            title="Sales"
                            color="success"
                            icon="ki-chart-line-up-2"
                            count={summary.totalSalesCount}
                            amount={summary.totalSalesAmount - (summary.totalSalesTax || 0)}
                            tax={summary.totalSalesTax}
                            total={summary.totalSalesAmount}
                        />
                        <SummaryCard
                            title="Purchases"
                            color="primary"
                            icon="ki-shop"
                            count={summary.totalPurchaseCount}
                            amount={summary.totalPurchaseAmount - (summary.totalPurchaseTax || 0)}
                            tax={summary.totalPurchaseTax}
                            total={summary.totalPurchaseAmount}
                        />
                        <SummaryCard
                            title="Expenses"
                            color="warning"
                            icon="ki-wallet"
                            count={summary.totalExpenseCount}
                            amount={summary.totalExpenseAmount}
                            tax={0}
                            total={summary.totalExpenseAmount}
                        />
                    </div>

                    {/* Net Summary */}
                    <div className="card mb-5" id="print-net-summary">
                        <div className="card-body p-6">
                            <div className="d-flex align-items-center mb-4">
                                <h5 className="fw-bold mb-0">Net Summary</h5>
                                <SectionPrintButton sectionId="print-net-summary" />
                            </div>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="d-flex justify-content-between p-3 bg-light-success rounded mb-2">
                                        <span className="text-gray-700 fw-semibold">Total Income (Sales)</span>
                                        <span className="fw-bolder text-success">{formatCurrency(summary.totalIncome)}</span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="d-flex justify-content-between p-3 bg-light-danger rounded mb-2">
                                        <span className="text-gray-700 fw-semibold">Total Expenditure</span>
                                        <span className="fw-bolder text-danger">{formatCurrency(summary.totalExpenditure)}</span>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className={`d-flex justify-content-between p-3 rounded mb-2 ${summary.netAmount >= 0 ? 'bg-light-success' : 'bg-light-danger'}`}>
                                        <span className="text-gray-700 fw-semibold">Net Amount</span>
                                        <span className={`fw-bolder ${summary.netAmount >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {formatCurrency(summary.netAmount)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detail Table */}
                    <div className="card mb-5" id="print-breakdown">
                        <div className="card-body p-6">
                            <div className="d-flex align-items-center mb-4">
                                <h5 className="fw-bold mb-0">Breakdown</h5>
                                <SectionPrintButton sectionId="print-breakdown" />
                            </div>
                            <div className="table-responsive">
                                <table className="table table-row-bordered table-row-gray-200 align-middle gs-3 gy-3">
                                    <thead>
                                        <tr className="fw-bold text-muted bg-light">
                                            <th className="ps-4">Type</th>
                                            <th className="text-end">Transactions</th>
                                            <th className="text-end">Amount</th>
                                            <th className="text-end">Tax</th>
                                            <th className="text-end pe-4">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(reportData.items || []).map((item, index) => (
                                            <tr key={index}>
                                                <td className="ps-4">
                                                    <span className={`badge badge-light-${item.type === 'Sales' ? 'success' : item.type === 'Purchases' ? 'primary' : 'warning'} fw-bold`}>
                                                        {item.type}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-semibold">{item.orderCount || 0}</td>
                                                <td className="text-end">{formatCurrency(item.totalAmount)}</td>
                                                <td className="text-end">{formatCurrency(item.totalTaxAmount)}</td>
                                                <td className="text-end pe-4 fw-bold">{formatCurrency(item.totalInvoiceAmount)}</td>
                                            </tr>
                                        ))}
                                        {(!reportData.items || reportData.items.length === 0) && (
                                            <tr>
                                                <td colSpan={5} className="text-center text-muted py-5">
                                                    No transactions found for this date
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {reportData.items && reportData.items.length > 0 && (
                                        <tfoot>
                                            <tr className="fw-bolder bg-light">
                                                <td className="ps-4">TOTAL</td>
                                                <td className="text-end">
                                                    {reportData.items.reduce((sum, i) => sum + (i.orderCount || 0), 0)}
                                                </td>
                                                <td className="text-end">
                                                    {formatCurrency(reportData.items.reduce((sum, i) => sum + (i.totalAmount || 0), 0))}
                                                </td>
                                                <td className="text-end">
                                                    {formatCurrency(reportData.items.reduce((sum, i) => sum + (i.totalTaxAmount || 0), 0))}
                                                </td>
                                                <td className="text-end pe-4">
                                                    {formatCurrency(reportData.items.reduce((sum, i) => sum + (i.totalInvoiceAmount || 0), 0))}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Sale Items Breakdown */}
                    <ItemBreakdownTable
                        title="Sale Items Breakdown"
                        color="success"
                        icon="ki-chart-line-up-2"
                        items={reportData.saleItemBreakdown}
                    />

                    {/* Purchase Items Breakdown */}
                    <ItemBreakdownTable
                        title="Purchase Items Breakdown"
                        color="primary"
                        icon="ki-shop"
                        items={reportData.purchaseItemBreakdown}
                    />

                    {/* Ledger Amount Changes */}
                    {reportData.ledgerChanges && reportData.ledgerChanges.length > 0 && (
                        <LedgerChangesTable ledgerChanges={reportData.ledgerChanges} />
                    )}

                    {/* Individual Sales */}
                    {reportData.salesDetails && reportData.salesDetails.length > 0 && (
                        <DetailsTable
                            title="Sales Details"
                            color="success"
                            icon="ki-chart-line-up-2"
                            items={reportData.salesDetails}
                            showTransaction
                            showTax
                            showCollected
                            showLedger
                        />
                    )}

                    {/* Individual Purchases */}
                    {reportData.purchaseDetails && reportData.purchaseDetails.length > 0 && (
                        <DetailsTable
                            title="Purchase Details"
                            color="primary"
                            icon="ki-shop"
                            items={reportData.purchaseDetails}
                            showTransaction
                            showTax
                            showLedger
                        />
                    )}

                    {/* Individual Expenses */}
                    {reportData.expenseDetails && reportData.expenseDetails.length > 0 && (
                        <DetailsTable
                            title="Expense Details"
                            color="warning"
                            icon="ki-wallet"
                            items={reportData.expenseDetails}
                            showLedger
                        />
                    )}

                    {/* Print Footer */}
                    <div className="print-only text-center mt-6">
                        <div className="separator my-4"></div>
                        <div className="text-gray-500 fs-7">
                            Generated on {formatDateTime(new Date().toISOString())}
                        </div>
                    </div>
                </>
            )}

            {/* Empty State */}
            {!reportData && !loading && !error && (
                <div className="card">
                    <div className="card-body p-10 text-center">
                        <i className="ki-duotone ki-document fs-3x text-gray-400 mb-5">
                            <span className="path1"></span>
                            <span className="path2"></span>
                        </i>
                        <h4 className="text-gray-600 mb-2">Daily Summary Report</h4>
                        <p className="text-gray-500 fs-6">
                            Select a date and click "Sync & Generate Report" to view the daily summary.
                        </p>
                    </div>
                </div>
            )}
        </>
    )
}

export {DailyReportPage}
